import React, { useEffect, useState, useCallback } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { FormControlGroup, FormControlLabel, Typography } from "src/components";
import { useNavigation } from "@react-navigation/native";
import { Button } from "src/components";
import { EventRegister } from "react-native-event-listeners";
import { navigationRef } from "src/hooks/navigation";
import {
  statusListWithAll,
  categoryListWithAll,
  genderListWithAll,
  prefectureListWithAll,
  ageListWithAll,
  orderList,
} from "src/constant";
import { useHomeFilter, IHomeFilter } from "src/hooks/home-filter";
import { Gender, ServiceOrder, UserStatus } from "src/domains";
import LocalText from "src/local";
import { RootStackScreenProps } from "src/navigation/types";

export const StatusControl: React.FC<{
  value?: UserStatus;
  onChange: (value?: UserStatus) => void;
}> = ({ value, onChange }) => {
  useEffect(() => {
    const listener = EventRegister.addEventListener(
      "HomeFilterStatus",
      (v: string) => {
        if (v === "ACTIVE") {
          onChange(UserStatus.ACTIVE);
        } else if (v === "OFFERABLE") {
          onChange(UserStatus.OFFERABLE);
        } else if (v === "LOGOUT") {
          onChange(UserStatus.LOGOUT);
        } else {
          onChange(undefined);
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  const getDetailLabel = () => {
    if (value === UserStatus.ACTIVE) {
      return LocalText.common.userStatusActive;
    } else if (value === UserStatus.OFFERABLE) {
      return LocalText.common.userStatusOfferable;
    } else if (value === UserStatus.LOGOUT) {
      return LocalText.common.userStatusLogout;
    }
    return LocalText.common.all;
  };
  const getKeyLabel = () => {
    if (value === UserStatus.ACTIVE) {
      return "ACTIVE";
    } else if (value === UserStatus.OFFERABLE) {
      return "OFFERABLE";
    } else if (value === UserStatus.LOGOUT) {
      return "LOGOUT";
    }
    return null;
  };
  return (
    <FormControlLabel
      text={LocalText.screens.homeFilter.userStatus}
      detail={getDetailLabel()}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "HomeFilterStatus",
          title: LocalText.screens.homeFilter.userStatus,
          selectedId: getKeyLabel(),
          items: statusListWithAll.map((item) => ({
            id: item.key,
            label: item.label,
          })),
        });
      }}
    />
  );
};

export const CategoryControl: React.FC<{
  values?: (string | null)[];
  onChange: (value?: string[]) => void;
}> = ({ values, onChange }) => {
  useEffect(() => {
    const listener = EventRegister.addEventListener(
      "HomeFilterCategory",
      (v: string[]) => {
        onChange(v.length > 0 ? v : undefined);
      }
    );
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  const getDetailLabel = () => {
    return values
      ? categoryListWithAll
          .filter((item) => values?.indexOf(item.key) !== -1)
          .map((item) => item.label)
          .join(", ")
      : LocalText.common.all;
  };
  return (
    <FormControlLabel
      text={LocalText.screens.homeFilter.category}
      detail={getDetailLabel()}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "HomeFilterCategory",
          title: LocalText.screens.homeFilter.category,
          selectedId: values,
          items: categoryListWithAll.map((item) => ({
            id: item.key,
            label: item.label,
          })),
          isMultiply: true,
        });
      }}
    />
  );
};

export const GenderControl: React.FC<{
  value?: Gender;
  onChange: (value?: Gender) => void;
}> = ({ value, onChange }) => {
  useEffect(() => {
    const listener = EventRegister.addEventListener(
      "HomeFilterGender",
      (v?: string) => {
        if (v === "MALE") {
          onChange(Gender.MALE);
        } else if (v === "FEMALE") {
          onChange(Gender.FEMALE);
        } else if (v === "UNDEFINED") {
          onChange(Gender.UNDEFINED);
        } else {
          onChange(undefined);
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  const getDetailLabel = () => {
    if (value === Gender.MALE) {
      return LocalText.common.genderMale;
    } else if (value === Gender.FEMALE) {
      return LocalText.common.genderFemale;
    } else if (value === Gender.UNDEFINED) {
      return LocalText.common.genderUndefined;
    }
    return LocalText.common.all;
  };
  const getDetailKey = () => {
    if (value === Gender.MALE) {
      return "MALE";
    } else if (value === Gender.FEMALE) {
      return "FEMALE";
    } else if (value === Gender.UNDEFINED) {
      return "UNDEFINED";
    }
    return null;
  };
  return (
    <FormControlLabel
      text={LocalText.screens.homeFilter.gender}
      detail={getDetailLabel()}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "HomeFilterGender",
          title: LocalText.screens.homeFilter.gender,
          selectedId: getDetailKey(),
          items: genderListWithAll.map((item) => ({
            id: item.key,
            label: item.label,
          })),
        });
      }}
    />
  );
};

export const AgeControl: React.FC<{
  values?: (string | null)[];
  onChange: (value?: string[]) => void;
}> = ({ values, onChange }) => {
  useEffect(() => {
    const listener = EventRegister.addEventListener(
      "HomeFilterAge",
      (v: string[]) => {
        onChange(v.length > 0 ? v : undefined);
      }
    );
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  const getDetailLabel = () => {
    return values
      ? ageListWithAll
          .filter((item) => values?.indexOf(item.key) !== -1)
          .map((item) => item.label)
          .join(", ")
      : LocalText.common.all;
  };
  return (
    <FormControlLabel
      text={LocalText.screens.homeFilter.age}
      detail={getDetailLabel()}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "HomeFilterAge",
          title: LocalText.screens.homeFilter.age,
          selectedId: values,
          items: ageListWithAll.map((item) => ({
            id: item.key,
            label: item.label,
          })),
          isMultiply: true,
        });
      }}
    />
  );
};

export const PrefectureControl: React.FC<{
  values?: (string | null)[];
  onChange: (value?: string[]) => void;
}> = ({ values, onChange }) => {
  useEffect(() => {
    const listener = EventRegister.addEventListener(
      "HomeFilterPrefecture",
      (v: string[]) => {
        onChange(v.length > 0 ? v : undefined);
      }
    );
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  const getDetailLabel = () => {
    return values
      ? prefectureListWithAll
          .filter((item) => values?.indexOf(item.key) !== -1)
          .map((item) => item.label)
          .join(", ")
      : LocalText.common.all;
  };
  return (
    <FormControlLabel
      text={LocalText.screens.homeFilter.area}
      detail={getDetailLabel()}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "HomeFilterPrefecture",
          title: LocalText.screens.homeFilter.area,
          selectedId: values,
          items: prefectureListWithAll.map((item) => ({
            id: item.key,
            label: item.label,
          })),
          isMultiply: true,
        });
      }}
    />
  );
};

export const OrderControl: React.FC<{
  value?: ServiceOrder;
  onChange: (value?: ServiceOrder) => void;
}> = ({ value, onChange }) => {
  useEffect(() => {
    const listener = EventRegister.addEventListener(
      "HomeFilterOrder",
      (v?: string) => {
        if (v === ServiceOrder.SERVICE_UPDATED) {
          onChange(ServiceOrder.SERVICE_UPDATED);
        } else if (v === ServiceOrder.SERVICE_USAGE_COUNT) {
          onChange(ServiceOrder.SERVICE_USAGE_COUNT);
        } else if (v === ServiceOrder.SERVICE_RECOMMENDATION) {
          onChange(ServiceOrder.SERVICE_RECOMMENDATION);
        } else {
          onChange(undefined);
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  const getLabel = () => {
    return value
      ? orderList.find((item) => item.key === value)?.label
      : LocalText.common.all;
  };
  return (
    <FormControlLabel
      text={getLabel() || ""}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "HomeFilterOrder",
          title: LocalText.screens.homeFilter.order,
          selectedId: value,
          items: orderList.map((item) => ({
            id: item.key,
            label: item.label,
          })),
        });
      }}
    />
  );
};

export const HomeFilterScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"HomeFilter">["navigation"]>();
  const [defaultFilter, , handleSetFilter, handleResetFilter] = useHomeFilter();
  const [filter, setFilter] = useState<IHomeFilter>(defaultFilter);
  const handleReset = useCallback(() => {
    handleResetFilter();
    navigation.goBack();
  }, [handleResetFilter, navigation]);
  const handleSet = useCallback(() => {
    handleSetFilter(filter);
    navigation.goBack();
  }, [handleSetFilter, filter, navigation]);
  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView}>
        <FormControlGroup>
          <StatusControl
            value={filter.filter?.ownerStatus}
            onChange={(v) => {
              let localFilter = { ...filter };
              localFilter.filter = { ...localFilter.filter, ownerStatus: v };
              setFilter(localFilter);
            }}
          />
          <CategoryControl
            values={filter.filter?.categories}
            onChange={(v) => {
              let localFilter = { ...filter };
              localFilter.filter = { ...localFilter.filter, categories: v };
              setFilter(localFilter);
            }}
          />
          <GenderControl
            value={filter.filter?.gender}
            onChange={(v) => {
              let localFilter = { ...filter };
              localFilter.filter = { ...localFilter.filter, gender: v };
              setFilter(localFilter);
            }}
          />
          <AgeControl
            values={filter.filter?.age}
            onChange={(v) => {
              let localFilter = { ...filter };
              localFilter.filter = { ...localFilter.filter, age: v };
              setFilter(localFilter);
            }}
          />
          <PrefectureControl
            values={filter.filter?.prefectures}
            onChange={(v) => {
              let localFilter = { ...filter };
              localFilter.filter = { ...localFilter.filter, prefectures: v };
              setFilter(localFilter);
            }}
          />
        </FormControlGroup>
        <Typography
          text={LocalText.screens.homeFilter.order}
          variant={"section"}
        />
        <FormControlGroup>
          <OrderControl
            value={filter.order}
            onChange={(v) => {
              let localFilter = { ...filter };
              localFilter.order = v || ServiceOrder.SERVICE_RECOMMENDATION;
              setFilter(localFilter);
            }}
          />
        </FormControlGroup>
      </ScrollView>
      <View style={styles.actionContainer}>
        <Button
          containerStyle={styles.leftActionContainer}
          variant={"secondary"}
          title={LocalText.screens.homeFilter.reset}
          onPress={handleReset}
        />
        <View style={styles.rightActionContainer}>
          <Button
            variant={"primary"}
            title={LocalText.common.save}
            onPress={handleSet}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    marginTop: 8,
  },
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollView: {
    backgroundColor: colors.gray1,
  },
  actionContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 16,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    backgroundColor: colors.white,
  },
  leftActionContainer: {
    flex: 1,
    marginRight: 8,
  },
  rightActionContainer: {
    flex: 1,
    marginLeft: 8,
  },
});
